import {useRef} from 'react';
import {useDispatch} from 'react-redux';

import {isNotNil} from 'ramda';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {omneticApi} from '@dms/api/base';
import {
  ACCOUNTING_SUBJECT_VOCABULARY,
  BackgroundNotificationAccountingSubjectChangedPayload,
} from '@dms/api/core';
import {notificationTypes, refreshDmsDatagrid} from '@dms/shared';

import {useDebouncedCallback} from 'shared/web';

import {DOCUMENT_DATA_GRID_CODES} from '../constants/DOCUMENT_DATA_GRID_CODES';
import {findAccountingSubjectContextId} from '../utils/findAccountingSubjectContextId';
import {isAccountingSubjectChangeForOrder} from '../utils/isAccountingSubjectChangeForOrder';

const REFETCH_DEBOUNCE_MS = 500;
const DOCUMENT_CHANGE_REASONS: BackgroundNotificationAccountingSubjectChangedPayload['reason'][] = [
  ACCOUNTING_SUBJECT_VOCABULARY.reasons.DOCUMENT_BOUND,
  ACCOUNTING_SUBJECT_VOCABULARY.reasons.PRINTOUT_ATTACHED,
];

interface UseAccountingDoorbellArgs {
  businessCaseId: string;
  checkoutId: string;
  orderId: string;
}

/**
 * Repaints the payment screen when the external accounting changes the order.
 *
 * Under `AccountingMode::External` the accounting chain is asynchronous — draft,
 * finalized document and printout arrive minutes after the user issued the
 * payment, and payments get paid or documents bound with no user action at all.
 * The backend rings `accounting_subject-changed` for each of these; this hook
 * treats it strictly as a doorbell: it says WHICH order moved, and the screen
 * state is then read back from the endpoints. Nothing from the payload is
 * rendered, because the payload carries no version and no sequence number and
 * goes stale by design. The business case is refetched too, because the backend
 * mirrors the paid edge into it (state and activity).
 *
 * A single issuance typically rings several times over minutes, so refetches are
 * debounced; doorbells for other orders are dropped by
 * `isAccountingSubjectChangeForOrder`.
 *
 * MUST be mounted only for tenants holding `createExternalCheckoutDocuments` —
 * see `AccountingDoorbell`, which is its only call site.
 */
export const useAccountingDoorbell = (args: UseAccountingDoorbellArgs) => {
  const dispatch = useDispatch();
  // Debouncing collapses the doorbells of a burst into one refetch, but a burst
  // can span several payments of the order (deposit and balance progressing at
  // once), so every payment that rang is remembered rather than just the last.
  const pendingPaymentIdsRef = useRef(new Set<string>());
  const hasPendingDocumentChangeRef = useRef(false);

  const refetchOrder = useDebouncedCallback(() => {
    const paymentIds = Array.from(pendingPaymentIdsRef.current);
    const hasDocumentChanged = hasPendingDocumentChangeRef.current;
    pendingPaymentIdsRef.current.clear();
    hasPendingDocumentChangeRef.current = false;

    dispatch(
      omneticApi.util.invalidateTags([
        ...paymentIds.map((paymentId) => ({type: 'accountingOperation' as const, id: paymentId})),
        {type: 'offer' as const, id: args.orderId},
        {type: 'Checkout' as const, id: args.checkoutId},
        // The doorbell carries no document or prescription id, so every open document detail,
        // document payment list and payment prescription refetches.
        'invoice',
        'invoiceProforma',
        'correctiveTaxDocument',
        'invoicePayment',
        'paymentPrescription',
        {type: 'BusinessCaseDetail' as const, id: args.businessCaseId},
        {type: 'BusinessCaseActions' as const, id: args.businessCaseId},
        {type: 'BusinessCaseActivity' as const, id: args.businessCaseId},
        ...(hasDocumentChanged ? [{type: 'documentsCount' as const, id: args.businessCaseId}] : []),
      ])
    );

    // The document grids fetch their rows outside RTK Query; an unmounted grid ignores the refresh.
    if (hasDocumentChanged) {
      Object.values(DOCUMENT_DATA_GRID_CODES).forEach((gridCode) => refreshDmsDatagrid(gridCode));
    }
  }, REFETCH_DEBOUNCE_MS);

  useSubscribeToNotificationsQuery((notification) => {
    if (
      notification.type !== notificationTypes.ACCOUNTING_SUBJECT_CHANGED ||
      !isAccountingSubjectChangeForOrder(notification.payload, args)
    ) {
      return;
    }

    const paymentId = findAccountingSubjectContextId(
      notification.payload.context,
      ACCOUNTING_SUBJECT_VOCABULARY.contextTypes.PAYMENT_ID
    );
    if (isNotNil(paymentId)) {
      pendingPaymentIdsRef.current.add(paymentId);
    }
    if (DOCUMENT_CHANGE_REASONS.includes(notification.payload.reason)) {
      hasPendingDocumentChangeRef.current = true;
    }
    refetchOrder();
  });
};
